import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
// The page's own tokens and kit (masterplan credit-limits-v3, E13). Mona Sans is global: App.tsx imports
// StrategicKpisPage statically, so nothing is imported from skpi here.
import '../styles/clim/tokens.css';
import '../styles/clim/kit.css';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useCreditLimits } from '../hooks/useCreditLimits';
import { ClimTabs, climTabPanelProps, type ClimTab } from '../components/credit-limits/grid';
import { OverviewTab } from '../components/credit-limits/overview/OverviewTab';
// B4: the Requests tab; `?tab=requests&request=<id>` opens one request (the link of the request mails).
import { RequestsTab } from '../components/credit-limits/requests/RequestsTab';
// B5: the Approval rules tab (everybody reads, Finance edits)
import { RulesTab } from '../components/credit-limits/config/RulesTab';
import type { OverviewScope } from '../types/creditLimits';

export type CreditLimitsTab = 'overview' | 'requests' | 'rules';

const TABS: readonly ClimTab<CreditLimitsTab>[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'requests', label: 'Requests' },
    { id: 'rules', label: 'Approval rules' },
];

const PANEL_PREFIX = 'clim';

/** The tab a `?tab=` value names; anything else is the overview. */
export function tabFromParam(value: string | null): CreditLimitsTab {
    return value === 'requests' || value === 'rules' ? value : 'overview';
}

export function CreditLimitsPage() {
    const { canView, isLoading: permLoading } = usePagePermissions('/credit-limits');
    const [params, setParams] = useSearchParams();
    const tab = tabFromParam(params.get('tab'));
    const [scope, setScope] = useState<OverviewScope>('active');
    const { overview, roles, loading, error, lookupError, refresh } = useCreditLimits(scope);

    if (permLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
        );
    }

    if (!canView) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">You do not have access to this page.</p>
            </div>
        );
    }

    const selectTab = (next: CreditLimitsTab) => {
        const nextParams = new URLSearchParams(params);
        if (next === 'overview') nextParams.delete('tab');
        else nextParams.set('tab', next);
        // An open request belongs to the Requests tab only.
        if (next !== 'requests') nextParams.delete('request');
        setParams(nextParams, { replace: true });
    };

    return (
        <div className="p-6">
            <div className="clim clim-root">
                <div className="toolbar">
                    <h1 style={{ margin: 0, fontSize: 'var(--fs-h2)', lineHeight: 'var(--lh-h2)', fontWeight: 650, color: 'var(--text-strong)' }}>Credit Limits</h1>
                    <span className="tb-grow" />
                    <span className="muted">Amounts in CHF except the insurance limit (EUR).{overview ? ` As of ${overview.asOf}.` : ''}</span>
                </div>

                <ClimTabs tabs={TABS} value={tab} onChange={selectTab} label="Credit limits" idPrefix={PANEL_PREFIX} />

                <div {...climTabPanelProps(PANEL_PREFIX, tab)} className="clim-panel">
                    {tab === 'overview' && (
                        <OverviewTab
                            overview={overview}
                            roles={roles}
                            scope={scope}
                            onScopeChange={setScope}
                            loading={loading}
                            error={error}
                            lookupError={lookupError}
                            onRetry={refresh}
                        />
                    )}
                    {tab === 'requests' && <RequestsTab onChanged={refresh} />}
                    {tab === 'rules' && <RulesTab />}
                </div>
            </div>
        </div>
    );
}
